/**
 * Utility functions for Vercel Analytics
 * This file provides functions for tracking custom events in the application
 */

/**
 * Track a custom event with Vercel Analytics
 * @param {string} eventName - The name of the event to track
 * @param {object} properties - Additional properties to include with the event
 */
export const trackEvent = (eventName, properties = {}) => {
  try {
    // Check if window and vercelAnalytics exist and track is a function
    if (typeof window !== 'undefined' && window.va && typeof window.va.track === 'function') {
      window.va.track(eventName, properties);
    } else {
      // Log to console in development
      console.log(`[Analytics] Event: ${eventName}`, properties);
    }
  } catch (error) {
    // Silently handle any analytics errors to prevent app crashes
    console.error('[Analytics] Error tracking event:', error);
  }
};

/**
 * Track a page view with Vercel Analytics
 * @param {string} pageName - The name of the page being viewed
 * @param {object} properties - Additional properties to include with the event
 */
export const trackPageView = (pageName, properties = {}) => {
  trackEvent('page_view', {
    page_name: pageName,
    ...properties
  });
};

/**
 * Track a user action with Vercel Analytics
 * @param {string} actionName - The name of the action being performed
 * @param {object} properties - Additional properties to include with the event
 */
export const trackAction = (actionName, properties = {}) => {
  trackEvent('user_action', {
    action_name: actionName,
    ...properties
  });
};

/**
 * Track a feature usage with Vercel Analytics
 * @param {string} featureName - The name of the feature being used
 * @param {object} properties - Additional properties to include with the event
 */
export const trackFeatureUsage = (featureName, properties = {}) => {
  trackEvent('feature_usage', {
    feature_name: featureName,
    ...properties
  });
};

/**
 * Track an error with Vercel Analytics
 * @param {string} errorType - The type of error
 * @param {string} errorMessage - The error message
 * @param {object} properties - Additional properties to include with the event
 */
export const trackError = (errorType, errorMessage, properties = {}) => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage,
    ...properties
  });
};

/**
 * Common analytics events for the application
 */
export const AnalyticsEvents = {
  // Authentication events
  AUTH_LOGIN: 'auth_login',
  AUTH_LOGOUT: 'auth_logout',
  AUTH_SIGNUP: 'auth_signup',
  
  // Game events
  GAME_ADDED: 'game_added',
  GAME_EDITED: 'game_edited',
  GAME_DELETED: 'game_deleted',
  
  // Player events
  PLAYER_CREATED: 'player_created',
  PLAYER_UPDATED: 'player_updated',
  
  // Feature usage
  STATS_VIEWED: 'stats_viewed',
  SCHEDULE_VIEWED: 'schedule_viewed',
  PROFILE_VIEWED: 'profile_viewed',
  DASHBOARD_VIEWED: 'dashboard_viewed',
  
  // Performance analysis
  HITTING_STREAK_DETECTED: 'hitting_streak_detected',
  SLUMP_DETECTED: 'slump_detected',
  HOT_STREAK_DETECTED: 'hot_streak_detected'
};
